import React, { useCallback } from 'react'

import clsx from 'clsx'

import Accordion from 'components/Accordion'
import Grid from 'components/Grid'
import Paper from 'components/Paper'
import Rating from 'components/Rating'
import Typography from 'components/Typography'
import UserInfo from 'components/UserInfo'
import { EventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'

import RatingCriteria from '../RatingCriteria'
import ListingIconName from './components/ListingIconName'
import ReviewContent from './components/ReviewContent'
import ShareToolbox from './components/ShareToolbox'
import { GRID_VARIATION } from './constants'

import classes from './Card.module.scss'

const noop = () => {}

/** @type {import("./types").default} */
const Card = ({
  className,
  cons,
  content,
  created,
  hasSearch,
  isCompact,
  listingDetailUrl,
  listingName,
  listingLogoUrl,
  onExpandClick = noop,
  onExpandPanelClick = noop,
  onListingIconNameClick = noop,
  onShareButtonClick = noop,
  permalink,
  pros,
  rating = {},
  reviewer = {},
  title,
  vendorReply,
  withListingIconName,
  component,
  language,
  translationTitle,
  translationComments,
  translationPros,
  translationCons,
  withUserInfo = true,
  profileReviewCard = false,
  review,
  listingNumReviews,
}) => {
  const variation = isCompact ? 'compact' : 'default'
  const classNameVariation = `ReviewCard-${variation}`
  const gridVariation = isCompact ? GRID_VARIATION.compact : GRID_VARIATION.default
  const { down, up } = useViewport()
  const showTopButtons = isCompact ? up('lg') : up('md')
  const showBottomButtons = isCompact ? down('md') : down('sm')

  //NOTE: temporal test with qualified reviews
  const showEnglishByDefault = listingNumReviews < 75

  const handleExpandPanelClick =
    name =>
    (event, { isExpanded }) =>
      onExpandPanelClick(event, { isExpanded, name })

  const ShareableButtons = useCallback(
    () => (
      <Grid item className={classes.shareWrapper} xs={12}>
        <div className={classes.shareItem}>
          <Typography className={classes.shareLabel} variant="heading-sm">
            Share this review:
          </Typography>
          <ShareToolbox
            disabledButtons={{ link: !(content || pros || cons) }}
            url={permalink}
            onButtonClick={onShareButtonClick}
          />
        </div>
      </Grid>
    ),
    [content, pros, cons, permalink, onShareButtonClick]
  )

  const hasAppName = !withUserInfo && down('md')
  return (
    <Paper className={clsx('Card', classes.root, classes[classNameVariation], className)}>
      <Grid container>
        <Grid item xs={12} md={gridVariation.summary.container.md} lg={gridVariation.summary.container.lg}>
          <Grid container>
            {(withUserInfo || hasAppName) && (
              <Grid
                className={clsx(classes.userInfoWrapper, classes.borderLayout, classes[classNameVariation])}
                item
                xs={12}
                sm={6}
                md={gridVariation.summary.userInfo.md}
                lg={12}
              >
                {withUserInfo && (
                  <EventContext value={{ name: 'engagement_hover', engagementComponent: 'product_reviews_card-item' }}>
                    <UserInfo {...reviewer} sentiment={rating.overall} userInfo={review?.user_info} />
                  </EventContext>
                )}
                {!withUserInfo && down('md') && (
                  <ListingIconName
                    href={listingDetailUrl}
                    name={listingName}
                    logoUrl={listingLogoUrl}
                    onClick={onListingIconNameClick}
                  />
                )}
              </Grid>
            )}
            <Grid
              className={clsx(classes.ratingWrapper, classes.borderLayout)}
              item
              xs={12}
              sm={6}
              md={gridVariation.summary.rating.md}
              lg={12}
            >
              <Accordion
                className={clsx(classes.ratingPanel, classes[classNameVariation])}
                summaryClassname={classes.ratingSummaryPanel}
                detailsClassname={clsx(classes.ratingDetailsPanel, classes[classNameVariation])}
                summary={
                  <Rating.CriteriaItem
                    average={rating.overall}
                    label="Overall rating"
                    labelVariation="heading-sm"
                    starSize="24"
                  />
                }
                details={<RatingCriteria fields={rating.byField} />}
                defaultExpanded
                disabled={up('sm')}
                onExpandClick={handleExpandPanelClick('overall-rating')}
              />
            </Grid>
            {showTopButtons && (
              <div>
                <ShareableButtons />
              </div>
            )}
          </Grid>
        </Grid>
        {withListingIconName && withUserInfo && (
          <Grid className={clsx(classes.borderLayout, classes.listingIconNameContainer)} item xs={12}>
            <ListingIconName
              className={classes.listingIconName}
              href={listingDetailUrl}
              name={listingName}
              logoUrl={listingLogoUrl}
              onClick={onListingIconNameClick}
            />
          </Grid>
        )}
        <Grid
          item
          className={clsx(classes.contentWrapper, classes[classNameVariation])}
          xs={12}
          md={gridVariation.content.md}
          lg={gridVariation.content.lg}
        >
          <ReviewContent
            component={component}
            hasSearch={hasSearch}
            cons={cons}
            content={content}
            created={created}
            listingDetailUrl={listingDetailUrl}
            listingName={listingName}
            listingLogoUrl={listingLogoUrl}
            onExpandClick={onExpandClick}
            onExpandPanelClick={onExpandPanelClick}
            onListingIconNameClick={onListingIconNameClick}
            pros={pros}
            title={title}
            vendorReply={vendorReply}
            withListingIconName={withListingIconName}
            language={language}
            translationTitle={translationTitle}
            translationComments={translationComments}
            translationPros={translationPros}
            translationCons={translationCons}
            profileReviewCard={profileReviewCard}
            review={review}
            showEnglishByDefault={showEnglishByDefault}
          />
        </Grid>
        {showBottomButtons && (
          <Grid item className={classes.shareWrapper} xs={12}>
            <ShareableButtons />
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}

export default Card
